import type { CommandHandler } from '../types';
import type { ColumnMetadata } from '../../types';
import { getChangeTotalState } from './changeTotal';
import {
  downloadFile,
  getColumnLabel,
  getExportColumns,
  getFooterValues,
  resolveCellValue,
} from './export';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TOTAL_STATES = new Set([-1, 0, 2]);

function normalize(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function getColumnField(column: ColumnMetadata): string {
  return column.dataField || column.field;
}

function findColumn(
  columns: ColumnMetadata[],
  names: string[],
  labels: string[]
): ColumnMetadata | undefined {
  const normalizedNames = names.map(normalize);
  const normalizedLabels = labels.map(normalize);
  return columns.find((column) => {
    const field = normalize(getColumnField(column));
    const label = normalize(getColumnLabel(column));
    return normalizedNames.includes(field) || normalizedLabels.some((item) => label.includes(item));
  });
}

function numericValue(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, '_');
}

function getTimesheetFilename(
  metadata: { dataSource?: string; contentType?: string },
  commands?: Array<{ alias: string; caption: string }>,
  pageTitle?: string
): string {
  const title = commands?.find((item) => item.alias.toLowerCase() === 'zgridtitle')?.caption;
  const entityName = pageTitle || title || metadata.dataSource || metadata.contentType || 'Export';
  const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
  return `${sanitizeFilenamePart(entityName)}_${timestamp}.xlsx`;
}

function parseHideTabs(settings: string | null): boolean {
  if (!settings) return false;
  try {
    const parsed = JSON.parse(settings) as { hideTabs?: unknown };
    return parsed.hideTabs === true;
  } catch {
    console.warn('[exportTimesheet] Invalid settings JSON');
    return false;
  }
}

function applyFormatting(
  worksheet: {
    getRow: (row: number) => {
      font: unknown;
      alignment: unknown;
      getCell: (column: number) => { border: unknown; value: unknown };
    };
    columns: Array<{ width?: number }>;
    rowCount: number;
    eachRow: (
      options: { includeEmpty: boolean },
      callback: (
        row: {
          eachCell: (callback: (cell: { alignment: unknown }) => void) => void;
        },
        rowNumber: number
      ) => void
    ) => void;
  },
  formatLastRow = true
) {
  const bold = { bold: true };
  const header = worksheet.getRow(1);
  header.font = bold;
  header.alignment = { horizontal: 'center' };

  worksheet.columns.forEach((column, index) => {
    column.width = 35;
    worksheet.getRow(1).getCell(index + 1).border = {
      top: { style: 'medium' },
      bottom: { style: 'medium' },
    };
  });

  if (formatLastRow && worksheet.rowCount > 1) {
    const last = worksheet.getRow(worksheet.rowCount);
    last.font = bold;
    last.alignment = { horizontal: 'center' };
    worksheet.columns.forEach((_column, index) => {
      const cell = last.getCell(index + 1);
      if (cell.value != null && cell.value !== '') {
        cell.border = { top: { style: 'medium' } };
      }
    });
  }

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber !== 1 && (!formatLastRow || rowNumber !== worksheet.rowCount)) {
      row.eachCell((cell) => {
        cell.alignment = { horizontal: 'center' };
      });
    }
  });
}

export const exportTimesheetCommandHandler: CommandHandler = async (command, context) => {
  const {
    metadata,
    displayData,
    columns,
    exportData = displayData,
    exportColumns: currentColumns = columns,
  } = context;

  if (!exportData?.length) {
    return { success: false, error: 'No data to export', shouldRefresh: false };
  }

  try {
    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const exportColumns = getExportColumns(currentColumns);
    const worksheetName =
      (metadata.dataSource || metadata.contentType || 'Sheet')
        .replace(/[*?:/\\[\]]/g, ' ')
        .trim()
        .slice(0, 31) || 'Sheet';
    const worksheet = workbook.addWorksheet(worksheetName);

    worksheet.columns = exportColumns.map((column) => ({
      header: getColumnLabel(column),
      key: getColumnField(column),
      width: 35,
    }));
    exportData.forEach((row) => {
      worksheet.addRow(
        exportColumns.map((column) =>
          resolveCellValue(row[getColumnField(column)], column, context.lookupMaps)
        )
      );
    });

    const footerValues = getFooterValues(exportData, exportColumns, context.serverSummary);
    if (footerValues) {
      worksheet.addRow(exportColumns.map((column) => footerValues.get(column) ?? ''));
    }

    applyFormatting(worksheet, Boolean(footerValues));

    const taskColumn =
      findColumn(
        exportColumns,
        ['Task', 'TaskName', 'ProjectName', 'Project'],
        ['task', 'project']
      ) ?? exportColumns[0];
    const hourColumn =
      findColumn(
        exportColumns,
        ['Hour', 'Hours', 'SpentHours', 'TaskSpentTime'],
        ['hour', 'hours']
      ) ?? exportColumns[4];
    const tagColumn = findColumn(exportColumns, ['Tag', 'TagName'], ['tag']) ?? exportColumns[6];

    if (
      !parseHideTabs(command.settings) &&
      TOTAL_STATES.has(getChangeTotalState(metadata.contentKey))
    ) {
      const taskTotals = new Map<string, number>();
      const taskTags = new Map<string, string>();
      const tagTotals = new Map<string, number>();

      exportData.forEach((row) => {
        const task = taskColumn ? String(row[getColumnField(taskColumn)] ?? '').trim() : '';
        const tag = tagColumn ? String(row[getColumnField(tagColumn)] ?? '').trim() : '';
        const hours = hourColumn ? numericValue(row[getColumnField(hourColumn)]) : 0;
        if (!task || normalize(task) === 'total') return;
        taskTotals.set(task, (taskTotals.get(task) ?? 0) + hours);
        if (tag && !taskTags.has(task)) taskTags.set(task, tag);
        if (tag) tagTotals.set(tag, (tagTotals.get(tag) ?? 0) + hours);
      });

      const byTask = workbook.addWorksheet('by Task');
      byTask.columns = [
        { header: 'Task', key: 'task' },
        { header: 'Hour', key: 'hour' },
        { header: 'Tag', key: 'tag' },
      ];
      taskTotals.forEach((hour, task) => {
        if (task.startsWith('OSKI_Task'))
          byTask.addRow({ task, hour, tag: taskTags.get(task) ?? '' });
      });
      byTask.addRow({
        task: 'Total',
        hour: [...taskTotals].reduce(
          (sum, [task, hour]) => (task.startsWith('OSKI_Task') ? sum + hour : sum),
          0
        ),
        tag: '',
      });

      const byTag = workbook.addWorksheet('by Tag');
      byTag.columns = [
        { header: 'Tag', key: 'tag' },
        { header: 'Hour', key: 'hour' },
      ];
      tagTotals.forEach((hour, tag) => byTag.addRow({ tag, hour }));
      byTag.addRow({
        tag: 'Total',
        hour: [...tagTotals.values()].reduce((sum, hour) => sum + hour, 0),
      });
      applyFormatting(byTask);
      applyFormatting(byTag);
    }

    const filename = getTimesheetFilename(metadata, context.commands, context.pageTitle);
    const buffer = await workbook.xlsx.writeBuffer();
    downloadFile(buffer, EXCEL_MIME, filename);
    return {
      success: true,
      message: `Exported ${exportData.length} rows to ${filename}`,
      shouldRefresh: false,
    };
  } catch (error: unknown) {
    console.error('[exportTimesheet] Export failed:', error);
    return {
      success: false,
      error: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      shouldRefresh: false,
    };
  }
};
